import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Stack,
  Select,
  Pagination,
  Avatar,
  Chip,
  Tooltip,
  Skeleton,
} from "@mantine/core";
import {
  IconSearch,
  IconRefresh,
  IconDeviceMobile,
  IconBrandAndroid,
  IconBrandApple,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import { usePerformRequest } from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";

interface IDeviceProps {
  id: string;
  os: string;
  maker: string;
  deviceModel: string;
  deviceId: string;
  osVersion: string;
  appVersion?: string;
  ipAddress?: string;
  lastLogin: string;
  createdAt: string;
  user?: {
    username?: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

const Devices = () => {
  useEffect(() => {
    document.title = "Devices | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [osFilter, setOsFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const devicesEndpoint = useMemo(
    () =>
      Endpoints.GetDevices({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        os: osFilter || undefined,
      }),
    [currentPage, pageSize, debouncedSearch, osFilter],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<IDeviceProps[]>
  >({
    method: devicesEndpoint.method,
    route: devicesEndpoint.route,
    showError: false,
  });

  const devices = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.toString();
    return date.toLocaleString();
  };

  const getOsBadge = (os: string) => {
    const isApple = os.toLowerCase() === "ios" || os.toLowerCase().includes("apple");
    return (
      <Badge
        color={isApple ? "dark" : "green"}
        variant="light"
        size="md"
        leftSection={isApple ? <IconBrandApple size={14} /> : <IconBrandAndroid size={14} />}
      >
        {os}
      </Badge>
    );
  };

  const rows = devices.map((device) => {
    const user = device.user;
    const userDisplayName = user
      ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || user.email
      : "—";

    return (
      <Table.Tr key={device.id}>
        <Table.Td>
          {user ? (
            <Group gap="sm">
              <Avatar size="sm" color="brand" radius="sm">
                {(user.firstName || user.email || "?").charAt(0)}
              </Avatar>
              <div>
                <Text fw={500} size="sm">
                  {userDisplayName}
                </Text>
                <Text size="xs" c="dimmed">
                  {user.email}
                </Text>
              </div>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">Unknown User</Text>
          )}
        </Table.Td>
        <Table.Td>{getOsBadge(device.os)}</Table.Td>
        <Table.Td>
          <Text size="sm" fw={500}>{device.maker}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{device.deviceModel}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed">{device.osVersion}</Text>
        </Table.Td>
        <Table.Td>
          <Tooltip label={device.deviceId} withArrow>
            <Text size="xs" style={{ fontFamily: "monospace" }}>
              {device.deviceId.substring(0, 12)}...
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td>
          <Text size="sm" style={{ fontFamily: "monospace" }}>{device.ipAddress || "—"}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatDate(device.lastLogin)}</Text>
        </Table.Td>
      </Table.Tr>
    );
  });

  const skeletonRows = Array.from({
    length: pageSize,
  }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Group gap="sm" align="center">
          <Skeleton height={32} circle />
          <div style={{ width: "60%" }}>
            <Skeleton height={10} width="80%" mb={6} />
            <Skeleton height={8} width="60%" />
          </div>
        </Group>
      </Table.Td>
      <Table.Td><Skeleton height={20} width={60} /></Table.Td>
      <Table.Td><Skeleton height={10} width="80%" /></Table.Td>
      <Table.Td><Skeleton height={10} width="70%" /></Table.Td>
      <Table.Td><Skeleton height={10} width={50} /></Table.Td>
      <Table.Td><Skeleton height={10} width="90%" /></Table.Td>
      <Table.Td><Skeleton height={10} width="80%" /></Table.Td>
      <Table.Td><Skeleton height={10} width="70%" /></Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="devices-page">
        <Container size="xl">
          <Stack gap="lg">
            <div>
              <Title order={1} className="page-title">
                Devices
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                Monitor registered user devices across Android and iOS
              </Text>
            </div>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" className="table-toolbar">
                <TextInput
                  placeholder="Search devices by maker, model, version, ip or user..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="md"
                />
                <Select
                  placeholder="Filter by OS"
                  data={[
                    { value: "Android", label: "Android" },
                    { value: "iOS", label: "iOS" },
                  ]}
                  value={osFilter}
                  onChange={(value) => {
                    setOsFilter(value);
                    setCurrentPage(1);
                  }}
                  clearable
                  size="md"
                  style={{ width: 180 }}
                />
                <Group gap="xs" className="quick-filters">
                  <Tooltip label="Show Android devices" withArrow>
                    <Chip
                      checked={osFilter === "Android"}
                      onChange={(checked) => {
                        setOsFilter(checked ? "Android" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      Android
                    </Chip>
                  </Tooltip>
                  <Tooltip label="Show iOS devices" withArrow>
                    <Chip
                      checked={osFilter === "iOS"}
                      onChange={(checked) => {
                        setOsFilter(checked ? "iOS" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      iOS
                    </Chip>
                  </Tooltip>
                </Group>
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    List Per Page
                  </Text>
                  <Select
                    data={["5", "10", "20", "50"]}
                    value={String(pageSize)}
                    onChange={(value) => {
                      const next = Number(value) || 10;
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    w={100}
                  />
                </Group>
              </Group>

              <div className="table-container">
                <Table
                  striped
                  highlightOnHover
                  verticalSpacing="sm"
                  className="data-grid"
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>User</Table.Th>
                      <Table.Th>OS</Table.Th>
                      <Table.Th>Maker</Table.Th>
                      <Table.Th>Model</Table.Th>
                      <Table.Th>OS Version</Table.Th>
                      <Table.Th>Device ID</Table.Th>
                      <Table.Th>IP Address</Table.Th>
                      <Table.Th>Last Login</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={8} style={{ textAlign: "center" }}>
                          <Stack align="center" gap="xs" py="xl">
                            <IconDeviceMobile size={40} stroke={1.5} color="gray" />
                            <Text c="dimmed">No devices found matching criteria</Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="center" mt="md">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => reloadData()}
                >
                  Reload Devices
                </Button>
              </Group>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e2e8f0" }}
              >
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} devices
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Container>
      </div>
    </PermissionGuard>
  );
};

export default Devices;
